import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Spin } from 'antd';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { ReactComponent as CloseIcon } from 'assets/images/icons/crossIcon.svg';
import { ReactComponent as EditIcon } from 'assets/images/icons/editIcon.svg';
import GuideStepThreeAlt from 'assets/images/tax-rate-guide-step3-alt.svg';
import { FormInput } from 'components/Custom/inputs';
import { MessageStates } from 'containers/MessageBox';
import moment from 'moment';
import { formatDisplayDate } from 'i18n/calendarLabels';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { getOrgLoading } from 'store/organisation/selectors';
import {
  deleteTaxRateTemplateRequest,
  editTaxRateTemplateNameRequest,
} from 'store/tax-rates/actions';
import { getTaxRateTemplates } from 'store/tax-rates/selectors';
import styled from 'styled-components';

interface CreateTaxTemplateProps {
  onSaveTemplate: (templateName: string) => void;
  editMode?: boolean;
}

const ImageContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;

  img {
    max-width: 100%;
    height: auto;
  }
`;

const GuideStep = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 320px;
  height: 100%;

  &:last-child ${ImageContainer} {
    margin-top: -5px;
  }
`;

const StepContent = styled.div`
  margin-bottom: 24px;
  min-height: 90px;
`;

const StepTitle = styled.h3`
  font-family: Inter;
  font-size: 18px;
  font-weight: 400;
  line-height: 25px;
  letter-spacing: 0%;
  color: var(--color-text-base, #2e2e2e);
  margin-bottom: 8px;
`;

const StepDescription = styled.p`
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0%;
  color: var(--color-text-muted, #5a6a6a);
  margin: 0;
`;

const Wrapper = styled.div`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;

  .label {
    font-size: 14px;
    color: var(--color-text-muted, #5a6a6a);
    margin-bottom: 8px;
    display: block;
  }
  .form-input {
    width: 100%;
    max-width: 370px;
  }
  .input-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
  }
  .input-wrapper {
    flex: 1;
    max-width: 370px;
  }
  .edit-controls {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .save-icon,
  .cancel-icon {
    width: 20px;
    height: 20px;
    cursor: pointer;
    transition: color 0.2s;
  }
  .save-icon {
    color: #52c41a;
  }
  .save-icon:hover {
    color: #389e0d;
  }
  .cancel-icon {
    color: #ff4d4f;
  }
  .cancel-icon:hover {
    color: #cf1322;
  }
  .templates-section {
    margin-top: 20px;
  }
  .section-title {
    font-size: 18px;
    font-weight: 400;
    color: var(--color-text-dark, #042a2a);
    margin-bottom: 16px;
  }
  .template-list {
    margin-bottom: 32px;
  }
  .template-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
    border-bottom: 1px solid var(--color-border, #dbeae3);
    cursor: pointer;
    transition: background-color 0.2s;
  }
  .template-item:hover {
    background-color: #f0f9fa;
  }
  .template-info {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .template-name {
    font-weight: 700;
    color: var(--color-text-dark, #042a2a);
    font-size: 16px;
    margin-bottom: 4px;
  }
  .template-date {
    font-family: Inter;
    font-weight: 400;
    font-size: 13px;
    color: var(--color-text-muted, #5a6a6a);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .edit-icon {
    color: var(--color-text-muted, #5a6a6a);
    width: 19px;
    height: 19px;
    cursor: pointer;
    transition: color 0.2s;
  }
  .edit-icon:hover {
    color: var(--color-primary, #41aaaf);
  }
  .eye-icon {
    color: var(--color-text-muted, #5a6a6a);
    width: 19px;
    height: 19px;
    font-size: 20px;
    cursor: pointer;
    transition: color 0.2s;
  }
  .eye-icon:hover {
    color: var(--color-primary, #41aaaf);
  }
  .close-icon {
    font-weight: 400;
    width: 17px;
    height: 17px;
    color: var(--color-text-muted, #5a6a6a);
    cursor: pointer;
    transition: color 0.2s;
  }
  .close-icon:hover {
    color: var(--color-primary, #41aaaf);
  }
  .scope-button {
    width: 100%;
    max-width: 370px;
    margin: 0 auto;
    display: block;
    margin-top: 48px;
  }
  .scope-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .my-templates-title {
    font-family: Inter;
    font-weight: 400;
    font-size: 18px;
    color: var(--color-text-dark, #042a2a);
    margin-bottom: 20px;
  }
  .template-name-title {
    font-family: Inter;
    font-weight: 600;
    font-size: 15px;
    line-height: 60px;
    letter-spacing: 0%;
    color: var(--color-text-dark, #042a2a);
  }
  .tax-rates-section {
    background-color: var(--color-surface, #f8fbfb);
    border-left: 2px solid var(--color-primary, #41aaaf);
    //margin: 8px 0;
    margin-left: 8px;
    padding: 4px 8px;
    border-radius: 0 4px 4px 0;
  }
  .tax-rates-bottom-border {
    border-bottom: 1px solid var(--color-border, #dbeae3);
    margin: 8px 0;
  }
  .tax-rates-title {
    font-family: Inter;
    font-weight: 600;
    font-size: 14px;
    color: var(--color-text-dark, #042a2a);
    margin-bottom: 12px;
  }
  .tax-rates-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .tax-rate-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e8f0f0;
  }
  .tax-rate-item:last-child {
    border-bottom: none;
  }
  .tax-rate-name {
    font-family: Inter;
    font-weight: 500;
    font-size: 13px;
    color: var(--color-text-dark, #042a2a);
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 12px;
  }
  .tax-rate-value {
    font-family: Inter;
    font-weight: 600;
    font-size: 13px;
    color: var(--color-primary, #41aaaf);
    flex-shrink: 0;
  }
  .no-tax-rates {
    font-family: Inter;
    font-weight: 400;
    font-size: 13px;
    color: var(--color-text-muted, #5a6a6a);
    font-style: italic;
  }
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;
  }
  .loading-text {
    margin-top: 16px;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
  }
  .tax-rates-section-wrapper {
    margin-top: 8px;
    max-height: 150px !important;
    overflow-y: auto !important;
    //scrollbar-width: thin;
    /* Thinner, modern scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--color-border, #dbeae3);
      border-radius: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

`;

const msgBoxWrapper: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 300,
  maxWidth: 300,
  minHeight: 290,
};

const textWrapper: React.CSSProperties = {
  paddingInlineStart: 3,
  maxWidth: 330,
  minHeight: 50,
};

export const CreateTaxTemplate: React.FC<CreateTaxTemplateProps> = ({
  onSaveTemplate,
  editMode = false,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [templateName, setTemplateName] = React.useState('');
  const [expandedTemplates, setExpandedTemplates] = React.useState<Set<string>>(
    new Set()
  );
  const [truncatedElements, setTruncatedElements] = React.useState<Set<string>>(
    new Set()
  );
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editingTemplateId, setEditingTemplateId] = React.useState<
    string | null
  >(null);

  // Get tax rate templates and loading state from Redux
  const taxRateTemplates = useSelector(getTaxRateTemplates);
  const loading = useSelector(getOrgLoading);

  // Transform tax rate templates to match the expected format
  const existingTemplates = React.useMemo(() => {
    return taxRateTemplates
      ? taxRateTemplates.map((template) => ({
          name: template.name,
          date: formatDisplayDate(
            template.createdDate,
            'DD/MM/YYYY',
            i18n.language
          ),
          id: template.id,
          taxRates: template.taxRates || [],
        }))
      : [];
  }, [i18n.language, taxRateTemplates]);

  // Check if text is truncated
  const checkTruncation = React.useCallback(
    (element: HTMLElement, id: string) => {
      const isTruncated = element.scrollWidth > element.clientWidth;
      setTruncatedElements((prev) => {
        const newSet = new Set(prev);
        if (isTruncated) {
          newSet.add(id);
        } else {
          newSet.delete(id);
        }
        return newSet;
      });
    },
    []
  );

  // Check truncation when templates are expanded
  React.useEffect(() => {
    if (expandedTemplates.size > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        document.querySelectorAll('.tax-rate-name').forEach((element) => {
          const id = element.getAttribute('data-tax-rate-id');
          if (id) {
            checkTruncation(element as HTMLElement, id);
          }
        });
      }, 100);
    }
  }, [expandedTemplates, checkTruncation]);

  const handleEditTemplate = (templateId: string, templateName: string) => {
    setIsEditMode(true);
    setEditingTemplateId(templateId);
    setTemplateName(templateName);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditingTemplateId(null);
    setTemplateName('');
  };

  const handleSaveEdit = () => {
    if (validateTemplateName(templateName) && editingTemplateId) {
      dispatch(
        editTaxRateTemplateNameRequest(editingTemplateId, templateName.trim())
      );
      setIsEditMode(false);
      setEditingTemplateId(null);
      setTemplateName('');
    }
  };

  const validateTemplateName = (name: string): boolean => {
    // Check if template name is empty
    if (!name || name.trim() === '') {
      const message: MessageData = {
        title: t('common.attention') + '!',
        description: t('organisation.template-name-empty'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return false;
    }

    // Check if template name already exists (case-insensitive) - exclude the current editing template
    const trimmedName = name.trim();
    const existingTemplate = existingTemplates.find(
      (template) =>
        template.name.trim().toLowerCase() === trimmedName.toLowerCase() &&
        template.id !== editingTemplateId
    );

    if (existingTemplate) {
      const message: MessageData = {
        title: t('common.attention') + '!',
        description: t('organisation.template-name-duplicate', {
          name: trimmedName,
        }),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return false;
    }

    return true;
  };

  const handleCreateTemplate = () => {
    if (validateTemplateName(templateName)) {
      onSaveTemplate(templateName.trim());
    }
  };

  const toggleTemplateExpansion = (templateId: string) => {
    const newExpandedTemplates = new Set(expandedTemplates);
    if (newExpandedTemplates.has(templateId)) {
      newExpandedTemplates.delete(templateId);
    } else {
      newExpandedTemplates.add(templateId);
    }
    setExpandedTemplates(newExpandedTemplates);
  };

  const handleTemplateClick = (e: React.MouseEvent, templateId: string) => {
    // Prevent expansion when clicking on action buttons
    const target = e.target as HTMLElement;
    if (target.closest('.actions')) {
      return;
    }
    toggleTemplateExpansion(templateId);
  };

  const handleDeleteTemplate = (templateId: string) => {
    dispatch(deleteTaxRateTemplateRequest(templateId));
  };

  return (
    <Wrapper>
      {/* Template name:
          - Always shown in create mode
          - In manage mode, only hidden in the special "no templates yet" case */}
      {(!editMode || existingTemplates.length > 0) && (
        <div>
          <label htmlFor="template-name" className="template-name-title">
            {t('organisation.template-name')}
          </label>
          <div className="input-container">
            <div className="input-wrapper">
              <FormInput
                id="template-name"
                className="form-input"
                placeholder={t('organisation.template-name')}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                disabled={editMode ? !isEditMode : false}
              />
            </div>
            {isEditMode && (
              <div className="edit-controls">
                <svg
                  className="save-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  onClick={handleSaveEdit}
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
                <svg
                  className="cancel-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  onClick={handleCancelEdit}
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </div>
            )}
          </div>
        </div>
      )}
      {editMode && (
        <div className="templates-section">
          {existingTemplates.length > 0 && (
            <div className="my-templates-title">{t('organisation.my-templates')}</div>
          )}
          {loading ? (
            <div className="loading-container">
              <Spin size="large" />
              <div className="loading-text">{t('organisation.loading-templates')}</div>
            </div>
          ) : existingTemplates.length === 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <GuideStep>
                <StepContent>
                  <StepTitle>
                    {t('organisation.guide-tax-rates-step3-title')}
                  </StepTitle>
                  <StepDescription>
                    {t('organisation.guide-tax-rates-step3-desc')}
                  </StepDescription>
                </StepContent>
                <ImageContainer>
                  <img
                    src={GuideStepThreeAlt}
                    alt={t('organisation.tax-template-guide-image-alt')}
                  />
                </ImageContainer>
              </GuideStep>
            </div>
          ) : (
            <div className="template-list">
              {existingTemplates.map((tpl) => {
                const templateId = tpl.id || tpl.name; // Use name as fallback if no id
                const isExpanded = expandedTemplates.has(templateId);

                return (
                  <div key={templateId}>
                    <div
                      className="template-item"
                      onClick={(e) => handleTemplateClick(e, templateId)}
                    >
                      <div className="template-info">
                        <span className="template-name">
                          {tpl.name} ({tpl.taxRates.length})
                        </span>
                        <span className="template-date">
                          {t('organisation.template-created-date', {
                            date: tpl.date,
                          })}
                        </span>
                      </div>
                      <div className="actions">
                        {isExpanded ? (
                          <EyeInvisibleOutlined
                            className="eye-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTemplateExpansion(templateId);
                            }}
                          />
                        ) : (
                          <EyeOutlined
                            className="eye-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTemplateExpansion(templateId);
                            }}
                          />
                        )}
                        <EditIcon
                          className="edit-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTemplate(templateId, tpl.name);
                          }}
                        />
                        <StyledPopconfirm
                          title={t('organisation.delete-template-title')}
                          description={t(
                            'organisation.delete-template-description',
                            { name: tpl.name }
                          )}
                          onConfirm={() => handleDeleteTemplate(templateId)}
                          okText={t('common.delete')}
                          cancelText={t('common.cancel')}
                          placement="topLeft"
                          overlayStyle={{
                            maxWidth: '400px',
                            width: '400px',
                          }}
                        >
                          <CloseIcon
                            className="close-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        </StyledPopconfirm>
                      </div>
                    </div>
                    {isExpanded && (
                      <div>
                        <div className="tax-rates-section-wrapper">
                          <div className="tax-rates-section">
                            {tpl.taxRates && tpl.taxRates.length > 0 ? (
                              <ul className="tax-rates-list">
                                {tpl.taxRates.map((taxRate) => (
                                  <li
                                    key={taxRate.id}
                                    className="tax-rate-item"
                                  >
                                    <span
                                      className="tax-rate-name"
                                      data-tax-rate-id={taxRate.id}
                                      title={
                                        truncatedElements.has(taxRate.id)
                                          ? taxRate.name
                                          : undefined
                                      }
                                    >
                                      {taxRate.name}
                                    </span>
                                    <span className="tax-rate-value">
                                      {taxRate.rate}%
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="no-tax-rates">
                                {t('organisation.no-tax-rates-for-template')}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="tax-rates-bottom-border"></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {!editMode && (
        <ScopeButton
          type="primary"
          className="scope-button"
          onClick={handleCreateTemplate}
          disabled={isEditMode}
        >
          {t('organisation.create-template')}
        </ScopeButton>
      )}
    </Wrapper>
  );
};
